import {
  Sparkles,
  Building2,
  Truck,
  ShieldCheck,
  Handshake,
  Search,
  MapPin,
  BadgeCheck,
  Repeat2,
  Boxes,
  MessagesSquare,
  CalendarClock,
} from "lucide-react";

export const PHONE_DISPLAY = "(336) 900-2307";
export const TEL_HREF = "tel:+13369002307";
export const SMS_HREF = "sms:+13369002307";
export const BUSINESS_NAME = "Authentically Yours Property Services LLC";
export const CITY = "Greensboro, NC";
export const BUSINESS_EMAIL = ""; // add business email here when available
export const FACEBOOK_URL =
  "https://www.facebook.com/share/1FSbxYTLfB/?mibextid=wwXIfr";
export const TIKTOK_URL = "https://www.tiktok.com/@authenticallyyoursps";
export const SERVICE_AREA_INTRO =
  "Based in Greensboro, proudly serving surrounding communities throughout the Triad and nearby areas.";

export const LOGO_LIGHT = "/assets/logo-light.png"; // for light backgrounds
export const LOGO_DARK = "/assets/logo-dark.png"; // for dark backgrounds

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Cleaning Services", to: "/cleaning-services" },
  { label: "Junk Removal", to: "/junk-removal" },
  { label: "Commercial", to: "/commercial" },
  { label: "About Us", to: "/about" },
  { label: "Our Work", to: "/our-work" },
  { label: "FAQs", to: "/faqs" },
  { label: "Service Areas", to: "/service-areas" },
  { label: "Contact", to: "/free-estimate" },
];

export const SERVICES = [
  {
    icon: Sparkles,
    title: "Residential & Commercial Cleaning",
    body:
      "Keep your space fresh, polished, and ready for the people who use it every day. We provide dependable cleaning solutions for residential properties and commercial spaces throughout the Greensboro area.",
    cta: "Explore Cleaning Services",
    to: "/cleaning-services",
  },
  {
    icon: Repeat2,
    title: "Move-In & Move-Out Cleaning",
    body:
      "Whether you're preparing a property for new occupants or leaving one behind, our detailed move-in and move-out cleaning services help create a clean, refreshed space that's ready for what comes next.",
    cta: "View Move-In/Move-Out Cleaning",
    to: "/cleaning-services",
  },
  {
    icon: Truck,
    title: "Junk Removal",
    body:
      "Clear unwanted items and reclaim valuable space with convenient junk removal for homes, rentals, businesses, turnovers, cleanouts, and more.",
    cta: "Explore Junk Removal",
    to: "/junk-removal",
  },
];

export const COMMERCIAL_BENEFITS = [
  { icon: Repeat2, label: "Apartment & Rental Turnovers" },
  { icon: Building2, label: "Commercial Cleaning" },
  { icon: Sparkles, label: "Move-In / Move-Out Services" },
  { icon: Boxes, label: "Property Cleanouts" },
  { icon: Truck, label: "Junk Removal" },
  { icon: CalendarClock, label: "Flexible Service Options" },
  { icon: MessagesSquare, label: "Reliable Communication" },
  { icon: BadgeCheck, label: "Free Estimates" },
];

export const WHY_CHOOSE = [
  { icon: ShieldCheck, title: "Insured", body: "Professional service with added peace of mind." },
  { icon: Handshake, title: "Reliable", body: "Clear communication and dependable service from start to finish." },
  { icon: Search, title: "Detail Focused", body: "We treat every space with care and attention." },
  { icon: MapPin, title: "Locally Owned", body: "Proudly serving Greensboro and the surrounding community." },
  { icon: Building2, title: "Residential & Commercial", body: "Flexible solutions for homeowners and professional property clients." },
  { icon: BadgeCheck, title: "Free Estimates", body: "Tell us what you need and we'll help you determine the right service." },
];

export const PROCESS = [
  { n: "01", title: "Tell Us What You Need", body: "Send us your property details and the service you're interested in." },
  { n: "02", title: "Get Your Free Estimate", body: "We'll review the project and provide an estimate based on your needs." },
  { n: "03", title: "Schedule Your Service", body: "Choose a time that works for your property or project." },
  { n: "04", title: "Enjoy the Difference", body: "We'll take care of the work so you can enjoy a cleaner, clearer space." },
];

export const FAQS = [
  { q: "Are you insured?", a: "Yes. We carry insurance for added peace of mind on every job." },
  { q: "Do you provide free estimates?", a: "Yes. Share details about your property and the service you need and we'll provide a free estimate." },
  { q: "Do I need to provide cleaning supplies?", a: "No — we bring what's needed for your service. If you'd prefer we use specific products you provide, just let us know." },
  { q: "What's the difference between Standard and Deep Cleaning?", a: "Standard Cleaning is a professional routine clean for generally maintained homes. Deep Cleaning includes everything in a Standard Clean plus detailed attention to baseboards, doors and frames, trim, window sills, buildup, fixtures and commonly missed areas — ideal for first-time cleans, seasonal resets or homes needing extra attention." },
  { q: "What is Maintenance Cleaning?", a: "Maintenance Cleaning is a recurring service (weekly, biweekly or monthly) for existing customers after an appropriate cleaning baseline has been established. It keeps your space consistently fresh and is not a lower-priced substitute for a Standard or Deep Clean." },
  { q: "Do you clean occupied and vacant properties?", a: "Yes. We clean both occupied homes and vacant units, including move-in/move-out and turnover situations." },
  { q: "Do you work with property managers and apartment communities?", a: "Absolutely. We welcome property managers, apartment communities, realtors, landlords and other property professionals, with flexible scheduling and customized scopes of work." },
  { q: "Can cleaning and junk removal be scheduled together?", a: "Yes. We can coordinate cleaning and junk removal together — especially helpful for move-outs, turnovers and cleanouts." },
  { q: "Do you offer recurring cleaning?", a: "Yes. Recurring Maintenance Cleaning is available weekly, biweekly or monthly." },
  { q: "Do I need to be home during service?", a: "It's up to you. Many clients aren't home during service; we'll simply coordinate access details with you in advance." },
  { q: "How should I prepare before my cleaning?", a: "No major prep needed. Picking up personal items and clearing surfaces helps us focus on cleaning, but we're happy to work with your space as-is." },
  { q: "Can I add inside-appliance cleaning?", a: "Yes. Inside refrigerator, oven, dishwasher, cabinets and more are available as add-ons through our \"Customize Your Clean\" options." },
  { q: "How is junk-removal pricing determined?", a: "Junk-removal pricing can depend on volume, weight, type of material, labor required, accessibility, number of trips, disposal fees and travel/distance when applicable. Photos help us provide an accurate estimate." },
  { q: "What items can't you haul?", a: "We can't accept hazardous or regulated materials, including chemicals, medical waste, asbestos-containing materials, explosives and other hazardous materials." },
  { q: "What areas do you service?", a: "We're based in Greensboro and proudly serve surrounding communities throughout the Triad and nearby areas. Not sure if you're covered? Call or text us." },
  { q: "How do I request an estimate?", a: "Use the website estimate form or call/text (336) 900-2307." },
];

export const OWNERS = [
  { name: "Kiarah Ayers", role: "Co-Owner" },
  { name: "Daryl Thomas", role: "Co-Owner" },
];

export const ABOUT_VALUES = [
  "Reliability",
  "Professionalism",
  "Clear Communication",
  "Attention to Detail",
  "Respect for Your Property",
  "Authentic Service",
];

export const CLEANING_TIERS = [
  {
    id: "standard",
    name: "Standard Cleaning",
    tagline: "For generally maintained homes needing a professional routine clean.",
    items: [
      "Kitchen countertops",
      "Sink & faucet",
      "Appliance exteriors",
      "Stovetop",
      "Backsplash",
      "Exterior cabinet surfaces as needed",
      "Bathrooms, toilets, tubs & showers",
      "Mirrors & accessible surfaces",
      "Dusting",
      "Vacuuming, sweeping & mopping",
      "Emptying interior trash bins & replacing liners",
      "General high-touch surface cleaning",
    ],
  },
  {
    id: "deep",
    name: "Deep Cleaning",
    tagline: "For first-time cleanings, seasonal resets, added buildup, or homes needing more detailed attention.",
    intro: "Includes everything in Standard Cleaning, plus:",
    items: [
      "Baseboards",
      "Doors & door frames",
      "Trim",
      "Window sills & accessible tracks",
      "Detailed cabinet fronts",
      "Extra kitchen grease/buildup attention",
      "Extra bathroom buildup & soap-scum attention",
      "Detailed fixtures",
      "Accessible ceiling fans & vents",
      "Switch plates & outlet covers",
      "Corners, edges & commonly missed areas",
    ],
  },
  {
    id: "maintenance",
    name: "Maintenance Cleaning",
    tagline: "A recurring service for existing customers, after an appropriate cleaning baseline has been established.",
    freq: ["Weekly", "Biweekly", "Monthly"],
    items: [
      "Routine kitchen upkeep",
      "Routine bathroom upkeep",
      "Floors",
      "Dusting",
      "General ongoing upkeep",
    ],
    footnote:
      "Maintenance Cleaning is primarily a recurring service — not a lower-priced substitute for Standard or Deep Cleaning.",
  },
];

export const CLEANING_ADDONS = [
  "Inside refrigerator",
  "Inside oven",
  "Inside dishwasher",
  "Inside cabinets",
  "Inside drawers",
  "Detailed blinds",
  "Interior windows",
  "Wall spot cleaning",
  "Detailed wall washing",
  "Additional specialty requests",
];

export const MOVE_SCOPE = [
  "Kitchens",
  "Bathrooms",
  "Floors",
  "Baseboards",
  "Doors & frames",
  "Closets & shelving",
  "Window sills & tracks",
  "Cabinet surfaces",
  "Fixtures",
  "Appliance exteriors",
  "General dust & debris removal",
];

export const AIRBNB_TURNOVER = {
  intro:
    "Designed for Airbnb, VRBO, furnished rentals, and other short-term rental properties. Our turnover service helps prepare your property for incoming guests with reliable, detailed, guest-ready cleaning between stays.",
  includes: [
    "Kitchen cleaning and sanitizing",
    "Countertops, sinks, and appliance exteriors",
    "Microwave interior",
    "Bathroom cleaning and sanitizing",
    "Toilets, tubs, showers, mirrors, and vanities",
    "Dusting accessible surfaces",
    "Wiping high-touch areas such as light switches, handles, and doorknobs",
    "Vacuuming and mopping floors",
    "Emptying trash and replacing liners",
    "Making beds with clean linens provided by the host",
    "Restocking host-provided supplies already available at the property",
    "General tidying and guest-ready presentation",
    "Checking for obvious items left behind by previous guests",
    "Reporting visible damage or maintenance concerns",
    "Photo documentation upon request",
  ],
  addons: [
    "Linen & Towel Laundering (Wash, Dry & Fold)",
    "Refrigerator interior cleaning",
    "Oven interior cleaning",
    "Interior window cleaning",
    "Blinds",
    "Baseboards",
    "Deep-Cleaning Services as Needed",
    "Supply Shopping/Pickup & Restocking",
    "Excessive trash removal",
    "Junk removal / cleanouts",
    "Same-day or priority turnover service, when available",
  ],
  addonsNote:
    "Add-on services are subject to property condition, time requirements, and availability. Additional charges may apply.",
};


export const JUNK_SERVICES = [
  "Household clutter",
  "Furniture",
  "Mattresses & box springs",
  "Bagged trash & debris",
  "Move-out debris",
  "Rental-property cleanouts",
  "Apartment cleanouts",
  "Garage cleanouts",
  "Storage-area cleanouts",
  "Office & commercial cleanouts",
  "Eligible outdoor items",
  "Other non-hazardous unwanted items",
];

export const JUNK_PRICING_FACTORS = [
  "Volume",
  "Weight",
  "Type of material",
  "Labor required",
  "Accessibility",
  "Number of trips",
  "Disposal fees",
  "Travel/distance when applicable",
];

export const JUNK_SAFETY =
  "For everyone's safety, hazardous or regulated materials may not be accepted — including chemicals, medical waste, asbestos-containing materials, explosives and other hazardous materials.";

export const COMMERCIAL_SEGMENTS = [
  {
    id: "turnovers",
    title: "Apartment & Rental Turnovers",
    items: [
      "Vacant-unit cleaning",
      "Move-out cleaning",
      "Turn preparation",
      "Junk & debris removal",
      "Cleaning + junk-removal coordination when appropriate",
    ],
  },
  {
    id: "property-managers",
    title: "Property Managers & Apartment Communities",
    items: [
      "Individual unit turnovers",
      "Multiple-property needs",
      "Customized scopes of work",
      "Flexible scheduling",
      "Cleaning & cleanout services",
    ],
  },
  {
    id: "realtors",
    title: "Realtors",
    items: [
      "Pre-listing cleaning",
      "Move-in cleaning",
      "Move-out cleaning",
      "Vacant-property cleaning",
      "Property cleanouts",
    ],
  },
  {
    id: "commercial",
    title: "Commercial Cleaning",
    items: [
      "Offices & commercial spaces",
      "One-time service",
      "Recurring service",
      "Customized scopes based on property needs",
    ],
  },
];

export const SERVICE_FILTERS = [
  "All",
  "Kitchens",
  "Bathrooms",
  "Move-Outs",
  "Detailed Cleaning",
  "Windows/Blinds",
  "Property Turnovers",
  "Junk Removal",
];
